import { useTranslation } from 'react-i18next';
import { Typography } from 'app/components/ui';
import './Footer.scss';
import { TaxonomyChild, TagWithMetadata } from 'entities/tag';
import { useTagSelection } from 'app/state/contexts/TagContext';
import { isFractionConceptType } from 'app/utils/tag-utils';
import { isEscapedConceptType } from 'app/utils/tag-utils/getConceptNarrowType';
import { useTagging } from './useTagging';
import { FooterActionsMenu } from './FooterActionsMenu';
import { ScopeToggle } from './ScopeToggle';
import { ContinuationModal } from '../Modal/ContinuationModal';

interface TaxonomyFooterProps {
  selectedTag: TaxonomyChild | null;
  selectedLinkroleUri?: string | null;
  isContentMarked: boolean;
  isTagUsed?: boolean;
  documentTags: TagWithMetadata[];
  onExtensionClick?: (tagId: string) => void;
  onHiddenTagCreated?: (tag: unknown) => void;
  onCellTagsApplied?: (tagIds: string[]) => void;
}

export const TaxonomyFooter = ({
  selectedTag,
  selectedLinkroleUri,
  isContentMarked,
  isTagUsed,
  documentTags,
  onExtensionClick,
  onHiddenTagCreated,
  onCellTagsApplied,
}: TaxonomyFooterProps) => {
  const { t } = useTranslation('tag');

  const { blockSelection, blockScope, setBlockScope, tableSelection } = useTagSelection();
  const {
    handleTagging,
    handleHiddenTagging,
    handleExcludeContent,
    handleCreateFootnote,
    isBlockTagging,
    hasContentMarked,
    carrierHoldsConcept,
    showContinuationModal,
    handleContinuationConfirm,
    handleContinuationCancel,
  } = useTagging({
    selectedTag,
    selectedLinkroleUri,
    isContentMarked,
    documentTags,
    onCellTagsApplied,
  });

  const isTableScope = !!tableSelection?.element || blockSelection?.kind === 'table';
  const showScopeToggle = isTableScope;
  const effectiveScope = blockScope === 'table' ? 'table' : 'cell';

  const isTextBlockConcept = isEscapedConceptType(selectedTag?.conceptType);
  const showListScopeToggle = blockSelection?.kind === 'list' && isTextBlockConcept;
  const blockTagConceptMismatch = isBlockTagging && !!selectedTag && !isTextBlockConcept;
  const isFractionConcept = isFractionConceptType(selectedTag?.conceptType);

  let description: string | null = null;

  if (blockTagConceptMismatch) {
    description = t('footer.block.requires.textblock');
  } else if (carrierHoldsConcept) {
    description = t('footer.block.concept.on.carrier');
  } else if (hasContentMarked && selectedTag && !isTagUsed) {
    description = t('footer.ready.to.use');
  } else if (hasContentMarked && selectedTag && isTagUsed) {
    description = t('footer.already.used');
  } else if (hasContentMarked && !selectedTag) {
    description = t('footer.missing.tag');
  } else if (!hasContentMarked && selectedTag) {
    description = t('footer.no.content.marked');
  } else if (!hasContentMarked && !selectedTag) {
    description = t('footer.select.tag.and.mark.content');
  }

  return (
    <footer className="tags-footer bg-white">
      {description && (
        <div className="tags-footer__info">
          <Typography variant="body-3" color="#717077" fontWeight="semibold">
            {t('footer.type.taxonomy')}
          </Typography>

          <Typography variant="body-7" fontWeight="light">
            {description}
          </Typography>
        </div>
      )}

      {showScopeToggle && <ScopeToggle axis="table" scope={effectiveScope} onChange={(s) => setBlockScope?.(s)} />}
      {showListScopeToggle && <ScopeToggle axis="list" scope={blockScope === 'text' ? 'text' : 'block'} onChange={(s) => setBlockScope?.(s)} />}

      <div className="tags-footer__actions">
        <button className="btn btn-secondary tags-footer__button" onClick={() => selectedTag && onExtensionClick?.(selectedTag.id)} disabled={!selectedTag}>
          {t('extend.from.concept')}
        </button>
        <button
          className="btn btn-primary tags-footer__button"
          onClick={handleTagging}
          disabled={!selectedTag || !hasContentMarked || blockTagConceptMismatch || isFractionConcept || carrierHoldsConcept}
        >
          {t('use.tag')}
        </button>
        <FooterActionsMenu
          actions={[
            {
              key: 'hidden',
              label: t(isFractionConcept ? 'footer.tag-as-fraction' : 'hidden.tag-as-hidden'),
              disabled: !selectedTag || selectedTag.isAbstract || hasContentMarked,
              onClick: () => handleHiddenTagging(onHiddenTagCreated),
            },
            {
              key: 'exclude',
              label: t('exclude.create'),
              // Excludes wrap a text selection — concept-independent.
              disabled: !isContentMarked,
              onClick: handleExcludeContent,
            },
            {
              key: 'footnote',
              label: t('footnote.create'),
              disabled: !isContentMarked,
              onClick: handleCreateFootnote,
            },
          ]}
        />
      </div>

      <ContinuationModal show={showContinuationModal} onConfirm={handleContinuationConfirm} onCancel={handleContinuationCancel} />
    </footer>
  );
};
